<template>
  <div class="container">
    <Toolbar class="mt-2 mb-2">
      <template #start>
        <h2>
          <span v-if="isUpdate">
            {{ $t('equipmentTypePage.updateEquipmentType') }}
          </span>
          <span v-else>{{ $t('equipmentTypePage.createEquipmentType') }}</span>
        </h2>
      </template>
    </Toolbar>
    <form
      v-if="!success"
      @submit.prevent="validForm"
    >
      <div class="p-input mb-3">
        <label
          for="code"
          class="required-input"
        >{{
          $t('entity.equipmentType.code') }}</label>
        <InputText
          id="code"
          v-model.trim="v$.equipmentType.code.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.equipmentType.code.$error }"
          type="text"
          autocomplete="on"
        />
        <div
          v-for="error of v$.equipmentType.code.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label
          for="french-wording"
          class="required-input"
        >{{ $t('entity.equipmentType.frenchWording') }}</label>
        <InputText
          id="french-wording"
          v-model="v$.equipmentType.frenchWording.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.equipmentType.frenchWording.$error }"
          type="text"
          required
        />
        <div
          v-for="error of v$.equipmentType.frenchWording.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label
          for="english-wording"
          class="required-input"
        >{{ $t('entity.equipmentType.englishWording') }}</label>
        <InputText
          id="english-wording"
          v-model="v$.equipmentType.englishWording.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.equipmentType.englishWording.$error }"
          type="text"
          required
        />
        <div
          v-for="error of v$.equipmentType.englishWording.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>
      <Message
        v-if="errors"
        severity="error"
        :closable="false"
      >
        <ul class="list-none m-0 p-0">
          <li
            v-for="error of errors"
            :key="error"
          >
            {{ $t(error) }}
          </li>
        </ul>
      </Message>
      <div class="my-3 flex justify-content-between flex-wrap">
        <Button
          :label="$t('action.back')"
          icon="pi pi-arrow-left"
          class="p-button-secondary p-button-rounded p-button-outlined"
          @click="toEquipmentTypeTable"
        />
        <Button
          type="submit"
          :label="isUpdate ? $t('action.crud.update') : $t('action.crud.create')"
          icon="pi pi-save"
          class="p-button-rounded p-button-outlined"
          :disabled="saveButtonDisabled"
          :loading="isSaving"
        />
      </div>
    </form>
  </div>
</template>

<script>
import {useVuelidate} from "@vuelidate/core";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import Message from "primevue/message";
import {maxLength, required} from "@/i18n/i18nValidators";
import Toolbar from "primevue/toolbar";

export default {
  name: 'EquipmentTypeForm',
  components: { InputText, Button, Message, Toolbar },
  setup() {
    return {
      v$: useVuelidate()
    }
  },
  data() {
    return {
      equipmentType: {
        id: null,
        code: '',
        frenchWording: '',
        englishWording: ''
      },
      isUpdate: false,
      errors: null,
      success: false,
      isSaving: false
    }
  },
  validations() {
    return {
      equipmentType: {
        code: { required, maxLength: maxLength(10) },
        frenchWording: { required, maxLength: maxLength(100) },
        englishWording: { required, maxLength: maxLength(100) }
      }
    }
  },
  computed: {
    saveButtonDisabled() {
      return this.v$.$invalid;
    }
  },
  async mounted() {
    if (this.$route.params.id) {
      this.fetchEquipmentType(this.$route.params.id);
      this.isUpdate = true;
    }
  },
  methods: {
    async fetchEquipmentType() {
      try {
        this.equipmentType = await this.$equipmentTypeService.fetchEquipmentType(this.$route.params.id);
        if (!this.equipmentType) await this.$router.push("/equipmentType");
      } catch (error) {
        this.$emitter.emit('toast', {
          severity: 'error',
          message: error.response.data.map(error => this.$t(error)).join(', ')
        });
        await this.$router.push("/equipmentType");
      }
    },
    async validForm() {
      this.errors = null;
      const isFormValid = await this.v$.$validate();
      if (!isFormValid) return;

      this.isSaving = true;

      if (this.equipmentType.id) {
        await this.updateEquipmentType()
      } else {
        await this.createEquipmentType()
      }
    },
    async createEquipmentType() {
      try {
        const created = await this.$equipmentTypeService.createEquipmentType(this.equipmentType)
        if (created) {
          this.success = true;
          this.$emitter.emit('toast', { severity: 'success', message: 'equipmentTypePage.createSuccess' })
          this.errors = null;
          this.isSaving = false;
          this.toEquipmentTypeTable();
        }
      } catch (err) {
        this.success = false;
        this.errors = err.response.data;
        this.isSaving = false;
      }
    },
    async updateEquipmentType() {
      try {
        const updated = await this.$equipmentTypeService.updateEquipmentType(this.equipmentType)
        if (updated) {
          this.success = true;
          this.$emitter.emit('toast', { severity: 'success', message: 'equipmentTypePage.updateSuccess' })
          this.errors = null;
          this.isSaving = false;
          this.toEquipmentTypeTable();
        }
      } catch (err) {
        this.success = false;
        this.errors = err.response.data;
        this.isSaving = false;
      }
    },
    toEquipmentTypeTable() {
      this.$router.push({ name: 'equipmentType' })
    }
  }
}
</script>

<style lang="scss" scoped>

</style>
